<template>
  <article>
    <LoaderStack v-if="isLoading" />
    <template v-else>
      <AreaSolutionsAnnouncement
        v-if="solutionData?.announcement"
        :item-data="solutionData.announcement"
      />

      <AreaSolutionsBanner
        v-if="solutionData?.banner"
        :item-data="solutionData.banner"
      />

      <TheNavigation
        :trigger-id="triggerId"
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-menu-button="isShowMobileMenu = !isShowMobileMenu"
      >
        <template #right-side>
          <TheButton
            :id="isCN ? 'tracking_requestdemo' : undefined"
            :to="`/${locale}/marketplace/solutions/${solutionAlias}/request-demo`"
            :title="t('request-a-demo')"
            class="w-[160px] tablet:w-[180px]"
            @click="handleClickRequestDemo"
          >
            <span class="text-sm">
              {{ t('request-a-demo') }}
            </span>
          </TheButton>
        </template>
      </TheNavigation>

      <TheScrollTrigger
        v-for="(item, index) in contextComponents"
        :key="hash(item)"
        :offset="offset"
        @trigger="handleTrigger(item.meta?.navigable, index)"
      >
        <component
          :is="provideComponentName(item.component)"
          :id="provideSolutionsID(item.meta?.navigable, index)"
          :suite-data="item"
          url-connector="solution"
        />
      </TheScrollTrigger>

      <ContactSection
        :title="tGlobal('contact.title')"
        :description="tGlobal('contact.description')"
        :button-name="tGlobal('contact.item-button')"
        :button-url="`/${locale}/marketplace/help/contact-us`"
        :button-id="isCN ? 'tracking_contactUs' : undefined"
        @click-callback="handleClickContactUs"
      />

      <TheNavigationMenu
        :navigation="navigation"
        :is-show-mobile-menu="isShowMobileMenu"
        @click-item-button="isShowMobileMenu = false"
      />
    </template>
  </article>
</template>

<script setup lang="ts">
  import hash from 'object-hash';
  import type { ConcreteComponent } from 'vue';

  const Architecture = resolveComponent('SuiteArchitecture');
  const Dfsi = resolveComponent('SuiteDfsi');
  const Faq = resolveComponent('SuiteFaq');
  const ListIcon = resolveComponent('SuiteListIcon');
  const ListIconFeature = resolveComponent('SuiteListIconFeature');
  const ListIndicator = resolveComponent('SuiteListIndicator');
  const ListProduct = resolveComponent('SuiteListProduct');
  const Overview = resolveComponent('SuiteOverview');
  const Resources = resolveComponent('SuiteResources');
  const SuccessfulCase = resolveComponent('SuiteSuccessfulCase');
  const Suite = resolveComponent('SuiteSuite');
  const SuiteProduct = resolveComponent('SuiteProduct');

  const route = useRoute();
  const { t, locale } = useI18n({ useScope: 'local' });
  const { t: tGlobal } = useI18n({ useScope: 'global' });
  const { headerHeight, navigationHeight, provideWebTitle } = useWebsite();
  const { isCN } = useLanguage();
  const { arrived } = useScrollArrivedBottom();

  const solutionsStore = useSolutionsStore();

  const { solutionData, isShowMobileMenu, contextComponents, navigation } =
    storeToRefs(solutionsStore);

  const { solution, generic } = route.params;
  const solutionAlias = generic ? `${solution}/${generic}` : solution;

  const triggerId = ref<string>('');

  const offset = computed(
    (): number => headerHeight.value + navigationHeight.value
  );

  const { status: GET_SOLUTIONS_STATUS } =
    await solutionsStore.USE_GET_SOLUTIONS();

  const isLoading = computed(
    (): boolean => GET_SOLUTIONS_STATUS.value === 'pending'
  );

  const provideComponentName = (
    code: string | null
  ): string | ConcreteComponent => {
    switch (code) {
      case 'architecture':
        return Architecture;
      case 'faq':
        return Faq;
      case 'list-icon':
        return ListIcon;
      case 'list-icon-feature':
        return ListIconFeature;
      case 'list-indicator':
        return ListIndicator;
      case 'list-product':
        return ListProduct;
      case 'overview':
        return Overview;
      case 'resources':
        return Resources;
      case 'successful-case':
        return SuccessfulCase;
      case 'suite':
        return Suite;
      case 'suite-product':
        return SuiteProduct;
      case 'dfsi':
        return Dfsi;
      default:
        return '';
    }
  };

  const handleTrigger = (
    navigable: string | undefined,
    index: number
  ): void => {
    if (!arrived.value) {
      const id = provideSolutionsID(navigable, index);
      if (id && triggerId.value !== id) {
        triggerId.value = id;
      }
    }
  };

  const handleClickContactUs = (): void => {
    if (solutionAlias === 'iems-solutions') {
      gtagSolutionIEMS('contact-us', locale.value);
    }
  };

  const handleClickRequestDemo = (): void => {
    if (solutionAlias === 'iems-solutions') {
      gtagSolutionIEMS('request-demo', locale.value);
    }
    if (solutionData.value?.banner?.title) {
      window.gtag?.('event', 'site_click', {
        Item: 'RAD_Solution',
        Item_Name: solutionData.value.banner.title,
      });
    }
  };

  watch(arrived, (value) => {
    if (value) {
      const id = navigation.value[navigation.value.length - 1]?.id || '';
      triggerId.value = id;
    }
  });

  watch(
    [GET_SOLUTIONS_STATUS],
    (value) => {
      if (value.includes('error')) {
        showError({ statusCode: 404 });
      }
    },
    { immediate: true }
  );

  useSeoMeta({
    title: () => solutionData.value?.banner.title || '',
    ogTitle: () => provideWebTitle(solutionData.value?.banner.title),
    twitterTitle: () => provideWebTitle(solutionData.value?.banner.title),
    description: () => solutionData.value?.banner.description || '',
    ogDescription: () => solutionData.value?.banner.description || '',
    twitterDescription: () => solutionData.value?.banner.description || '',
  });
</script>

<i18n lang="json">
{
  "en-int": {
    "request-a-demo": "Request a demo"
  },
  "en-jp": {
    "request-a-demo": "Request a demo"
  },
  "en-tw": {
    "request-a-demo": "Request a demo"
  },
  "en-kr": {
    "request-a-demo": "Request a demo"
  },
  "en-americas": {
    "request-a-demo": "Request a demo"
  },
  "en-asia-pacific": {
    "request-a-demo": "Request a demo"
  },
  "en-emea": {
    "request-a-demo": "Request a demo"
  },
  "ja-jp": {
    "request-a-demo": "製品に関するお問い合わせ"
  },
  "zh-cn": {
    "request-a-demo": "申请产品咨询"
  },
  "zh-tw": {
    "request-a-demo": "申請產品諮詢"
  }
}
</i18n>
